'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { Impressum, ImpressumInput } from "@/app/lib/generated/graphql-types";
import { ImpressumEditable } from "@/app/features/sites/impressum/types/types";
import { ImpressumSchema } from "@/app/features/sites/impressum/types/schema";
import { getImpressum } from "@/app/features/sites/impressum/data-access/queries";
import { updateImpressum } from "@/app/features/sites/impressum/data-access/mutations";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";

const ImpressumEditorContext = createContext<UseSingleTypeEditorType<ImpressumEditable> | undefined>(undefined);

export function useImpressumEditor() {
    const context = useContext(ImpressumEditorContext);

    if (!context) {
        throw new Error("useImpressumEditor must be used within an ImpressumEditorProvider");
    }

    return context;
}

interface ImpressumEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function ImpressumEditorProvider({
    children,
    isDraftModeEnabled,
}: ImpressumEditorProviderProps) {
    const editor = useSingleTypeEditor<Impressum, ImpressumEditable, ImpressumInput>({
        schema: ImpressumSchema,
        loadEntity: getImpressum,
        update: updateImpressum,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <ImpressumEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={editor.discardChanges}
            />
        </ImpressumEditorContext.Provider>
    );
}
